import Maximize2 from 'assets/svg/icons/Maximize2';
import Minimize2 from 'assets/svg/icons/Minimize2';
import SpeechBubblePlus from 'assets/svg/icons/SpeechBubblePlus';
import classNames from 'classnames';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import { Helper } from 'components/molecules/Helper';
import {
  AIChatProvider,
  useAIChatContext,
} from 'components/organisms/AIChat/AIChat.context';
import AIChatDataShareModal from 'components/organisms/AIChat/AIChatDataShareModal';
import AIChatMessageItem from 'components/organisms/AIChat/AIChatMessageItem';
import AIChatSpinner from 'components/organisms/AIChat/AIChatMessageSkeleton';
import AIChatTextbox from 'components/organisms/AIChat/AIChatTextbox';
import AISuggestedQuestions from 'components/organisms/AISuggestedQuestions';
import useAIUsageConsent from 'hooks/shared/useAIUsageConsent';
import last from 'lodash/last';
import { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AIRoleEnum, SuggestedQuestionUseCaseEnum } from 'types/ai.types';
import { UserAiUsageConsentEnum } from 'types/users.types';
import { ExclamationIconOutline } from 'utils/icons';
import BorderGradient from '../../BorderGradient';
import AIChatYearlyHistory from './AIChatYearlyHistory';
import ChatSkeleton from './ChatSkeleton';

type AIChatYearlyProps = {
  rejectedConsent: boolean;
  setRejectedConsent: (value: boolean) => void;
};

const AIChatYearly: FC<AIChatYearlyProps> = ({
  rejectedConsent,
  setRejectedConsent,
}) => {
  const {
    message,
    currentTipId,
    conversation,
    handleNewChat,
    usage,
    isLoading,
    streamedData,
    handleSend,
  } = useAIChatContext();

  const hasMessages =
    conversation?.messages?.length || currentTipId || isLoading;

  const { t } = useTranslation();

  const [isMaximized, setIsMaximized] = useState(false);
  const [defaultPromptSent, setDefaultPromptSent] = useState(false);

  const {
    consent,
    patchConsent,
    isLoading: isConsentLoading,
  } = useAIUsageConsent({
    onUpdate: (consent) => {
      if (consent === UserAiUsageConsentEnum.allow) {
        handleSend(t('yearly_review.ai_chat.default_prompt'));
        setDefaultPromptSent(true);
        setRejectedConsent(false);
      }
    },
  });

  const allMessages = streamedData?.length
    ? conversation?.messages?.concat([
        { _id: 'stream', role: AIRoleEnum.assistant, content: streamedData },
      ])
    : conversation?.messages;

  const lastMessage = useMemo(() => last(allMessages), [allMessages]);

  return (
    <div
      className={classNames(
        'flex flex-col min-h-[500px] transition-all duration-300 flex-1 relative',
        {
          'h-[648px]': isMaximized,
          'h-[500px]': !isMaximized,
        },
      )}
    >
      <div className="flex items-center justify-between p-3 absolute top-0 left-0 right-0 z-10">
        {usage?.monthly_message_count > 20 && (
          <div className="shadow-[0px_1px_2px_0px_rgba(146,64,14,0.12),0px_0px_0px_1px_#FEF3C7] backdrop-blur-[6px] !mb-0 p-2 bg-[#FFFBEB] rounded-md mb-2 flex gap-2 items-center">
            <ExclamationIconOutline className="w-5 h-5 text-[#92400E]" />

            <span className="text-xs text-[#92400E]">
              {t('ai_bot.messages_this_month', {
                count: usage?.monthly_message_count,
                limit: usage?.monthly_message_limit,
              })}
            </span>
          </div>
        )}

        <div className="flex-1 flex justify-end gap-[10px]">
          {conversation || currentTipId ? (
            <Button
              onClick={handleNewChat}
              asDefault
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-primary-100 bg-white shadow-sm text-primary-700 hover:text-primary hover:border-primary hover:ring-2 hover:ring-primary-100 transition-all"
            >
              <SpeechBubblePlus className="w-6 h-6" />
            </Button>
          ) : null}

          <AIChatYearlyHistory />

          <Button
            onClick={() => setIsMaximized(!isMaximized)}
            asDefault
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-primary-100 bg-white shadow-sm text-primary-700 hover:text-primary hover:border-primary hover:ring-2 hover:ring-primary-100 transition-all"
          >
            {isMaximized ? (
              <Minimize2 className="w-6 h-6" />
            ) : (
              <Maximize2 className="w-6 h-6" />
            )}
          </Button>
        </div>
      </div>

      <div className="text-primary-700 relative pb-4 px-4 md:px-8 min-h-0 flex-1 flex flex-col text-sm">
        <div
          id="chat-box"
          className={classNames(
            'min-h-0 scrollbar-hide flex flex-col pt-20',
            consent === UserAiUsageConsentEnum.allow && defaultPromptSent
              ? 'overflow-y-auto'
              : 'overflow-y-hidden',
          )}
        >
          {!defaultPromptSent ? (
            <>
              <AIChatMessageItem
                isFullWidth={false}
                message={{
                  _id: 'stream',
                  role: AIRoleEnum.user,
                  content: t('yearly_review.ai_chat.default_prompt'),
                }}
              />

              {consent !== UserAiUsageConsentEnum.allow ? (
                <div className="mt-4 flex flex-col items-end gap-3">
                  <div className="flex items-center gap-1">
                    <p className="text-md text-primary">
                      {t('yearly_review.ai_chat.consent_text')}
                    </p>
                    <Helper>
                      {/* Main alert (same as settings) */}
                      <Helper.Title className="text-center !text-primary-800">
                        {t('ai_bot.data_share.alert.title')}
                      </Helper.Title>
                      <Helper.Paragraph className="whitespace-pre-line text-sm">
                        {t('ai_bot.data_share.alert.text', {
                          buttonText: t('ai_bot.data_share.helper.title'),
                        })}
                      </Helper.Paragraph>

                      {/* What data is shared */}
                      <Helper.Title className="text-center !text-primary-800 mt-4">
                        {t('ai_bot.data_share.helper.title')}
                      </Helper.Title>
                      <Helper.Paragraph>
                        {t('ai_bot.data_share.helper.subtitle')}
                      </Helper.Paragraph>
                      <Helper.Paragraph>
                        {[
                          'occupation',
                          'vat_type',
                          'aggregated_data',
                          'tax_deadlines',
                        ].map((key) => (
                          <p key={key}>- {t(`ai_bot.data_share.${key}`)}</p>
                        ))}
                      </Helper.Paragraph>

                      <Helper.Paragraph>
                        {t('ai_bot.data_share.helper.no_personal_data')}
                      </Helper.Paragraph>
                    </Helper>
                  </div>
                  <div className="flex gap-4">
                    <Button
                      structure="primary"
                      color="taxes"
                      onClick={() => patchConsent(UserAiUsageConsentEnum.allow)}
                      loading={isConsentLoading}
                    >
                      {t('yearly_review.ai_chat.consent_accept')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex flex-col items-end gap-3">
                  <Button
                    structure="primary"
                    color="taxes"
                    onClick={() => {
                      handleSend(t('yearly_review.ai_chat.default_prompt'));
                      setDefaultPromptSent(true);
                    }}
                    loading={isLoading}
                  >
                    {t('yearly_review.ai_chat.default_prompt_button')}
                  </Button>
                </div>
              )}

              <ChatSkeleton />
            </>
          ) : (
            <div className="flex-1 flex flex-col gap-5">
              {allMessages?.map((message) => (
                <AIChatMessageItem
                  key={message.content}
                  isFullWidth={false}
                  message={message}
                  className="mt-0"
                  isLastAssistantMessage={
                    lastMessage?._id === message._id &&
                    lastMessage.role !== 'user'
                  }
                />
              ))}

              {isLoading && !streamedData && <AIChatSpinner />}

              {usage?.reachedMaxCount &&
              conversation?.status === 'processed' ? (
                <Alert
                  type="warning"
                  description={t('ai_bot.message_count_warning', {
                    limit: usage?.conversation_message_limit,
                  })}
                />
              ) : null}

              {!hasMessages && (
                <div className="h-full flex flex-col items-center">
                  <p className="text-primary text-lg mt-2 text-center">
                    {t('yearly_review.ai_chat.welcome_text')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {consent === UserAiUsageConsentEnum.allow &&
        defaultPromptSent &&
        !usage.reachedMaxMonthlyCount && (
          <>
            <AISuggestedQuestions
              use_case={SuggestedQuestionUseCaseEnum.wrapped}
              horizontal
            />

            <div className="bg-white rounded-xl flex flex-col px-8 pt-1">
              <AIChatTextbox
                className={classNames(!message?.includes('\n') ? 'py-3' : '', {
                  'cursor-not-allowed': usage.reachedMaxCount,
                  'opacity-80': usage.reachedMaxCount,
                })}
                placeholder={
                  currentTipId
                    ? t('ai_ask_follow_up_question')
                    : t('ai_send_placeholder')
                }
              />
              {usage.messageLengthExceeded && (
                <span className="text-red-500 text-xs inline-block mt-1">
                  {t('ai_bot.message_length_error', {
                    maxLength: 1500,
                  })}
                </span>
              )}
            </div>
          </>
        )}

      {consent === UserAiUsageConsentEnum.allow && defaultPromptSent && (
        <div className="text-center text-primary-400 flex items-center justify-between gap-2 px-8">
          <p className="text-xs">{t('ai.chat_review_disclaimer')} </p>
          <AIChatDataShareModal className="text-xs" />
        </div>
      )}
    </div>
  );
};

const AIChatYearlyWithProvider: FC<AIChatYearlyProps> = ({
  rejectedConsent,
  setRejectedConsent,
}) => (
  <AIChatProvider>
    <BorderGradient containerClassName="mt-8">
      <AIChatYearly
        rejectedConsent={rejectedConsent}
        setRejectedConsent={setRejectedConsent}
      />
    </BorderGradient>
  </AIChatProvider>
);

export default AIChatYearlyWithProvider;
